// src/services/dev-layout-diag.ts
//
// DEVELOPMENT ONLY, and only with ?diag=1 in the URL. Compiled out of production by the
// NODE_ENV guard in install().
//
// Renders the toolbar's layout measurements INTO the page. The numbers that identify a
// clipping fault -- which ancestor overflows, where the last button ends relative to the
// viewport -- are otherwise only reachable from a devtools console, and Playwright cannot
// reach Firefox on this machine (its bundled build is blocked from executing). A panel can be
// screenshotted, which is the one thing that always works.
//
// Reports live: it re-measures on resize and on any DOM change to the toolbar, so widening the
// window shows the exact point at which something starts overflowing.

const PANEL_ID = 'lux-layout-diag';

type Row = { label: string; value: string; bad?: boolean };

function measure(): Row[] {
  const el =
    document.querySelector<HTMLElement>('.view-switcher') ||
    document.querySelector<HTMLElement>('.v-btn-toggle');
  if (!el) return [{ label: 'switcher', value: 'not found on this page', bad: true }];

  const docW = document.documentElement.clientWidth;
  const buttons = [...el.querySelectorAll('button')];
  const last = buttons[buttons.length - 1];
  const lastRight = last ? Math.round(last.getBoundingClientRect().right) : NaN;

  // The first ancestor whose content is wider than its box is the thing pushing the row out.
  let culprit = '(none)';
  let n: HTMLElement | null = el;
  while (n && n !== document.documentElement) {
    if (n.scrollWidth > n.clientWidth + 1) {
      const cls = String(n.className || '').trim().split(/\s+/)[0] || '';
      culprit = `${n.tagName.toLowerCase()}${cls ? '.' + cls : ''} ${n.clientWidth}->${n.scrollWidth}`;
      break;
    }
    n = n.parentElement;
  }

  const clipped = Number.isFinite(lastRight) && lastRight > docW + 1;

  // The white band beside the map is the canvas lagging behind its container, so report both.
  // Firefox cannot be driven by Playwright on this machine, and this is the browser the fault
  // was reported in -- these two numbers are the whole diagnosis.
  const mapRows: Row[] = [];
  const container = document.querySelector<HTMLElement>('.map-container');
  const canvas = document.querySelector<HTMLElement>('canvas.mapboxgl-canvas');
  if (container && canvas) {
    const c = container.getBoundingClientRect();
    const k = canvas.getBoundingClientRect();
    const gap = Math.round(c.width - k.width);
    mapRows.push(
      { label: 'map container w', value: String(Math.round(c.width)) },
      { label: 'map canvas w', value: String(Math.round(k.width)), bad: Math.abs(gap) > 1 },
      { label: 'canvas gap', value: gap === 0 ? '0 (tracks)' : `${gap}px  <-- WHITE BAND`, bad: Math.abs(gap) > 1 },
      { label: 'container right', value: `${Math.round(c.right)} (doc ${docW})`, bad: Math.round(c.right) < docW - 1 },
    );
  } else {
    mapRows.push({ label: 'map', value: container ? 'canvas not found' : 'container not found', bad: true });
  }

  // Which ancestor is eating the width?
  //
  // The container ending short of the viewport, with the canvas tracking it exactly, means
  // something above it is narrower than the window -- in practice a vertical scrollbar being
  // reserved. Firefox draws a classic ~17px bar where Chrome shows none, so this is invisible
  // in the browser that can be automated here. Report the culprit by name rather than infer it.
  if (container) {
    let found = '(none)';
    let n: HTMLElement | null = container;
    while (n && n !== document.documentElement) {
      const bar = n.offsetWidth - n.clientWidth; // border + vertical scrollbar
      if (bar > 1) {
        const cls = String(n.className || '').trim().split(/\s+/)[0] || '';
        const s = getComputedStyle(n);
        found = `${n.tagName.toLowerCase()}${cls ? '.' + cls : ''} takes ${bar}px (overflow-y:${s.overflowY})`;
        break;
      }
      n = n.parentElement;
    }
    mapRows.push({ label: "width eaten by", value: found, bad: found !== "(none)" });

    // Vuetify pads <html> by the scrollbar width while an overlay blocks scrolling
    // (.v-overlay-scroll-blocked { padding-inline-end: var(--v-scrollbar-offset) }). Padding on
    // html shrinks body, and the offset is ~17px in Firefox but 0 in Chrome -- which would
    // explain a Gecko-only band. Report it rather than assume it.
    const hs = getComputedStyle(document.documentElement);
    mapRows.push(
      { label: "html class", value: document.documentElement.className || "(none)", bad: /scroll-blocked/.test(document.documentElement.className) },
      { label: "html padding-r", value: hs.paddingRight, bad: parseFloat(hs.paddingRight) > 1 },
      { label: "scrollbar-offset", value: hs.getPropertyValue("--v-scrollbar-offset").trim() || "(unset)" },
      { label: "overlays in DOM", value: String(document.querySelectorAll(".v-overlay").length) },
    );

    // No scrollbar and no overflow, yet the container stops short of the window. So an
    // ancestor is simply narrower, or offset. Print the chain and let the first row whose
    // right edge is not the document width identify itself, with the spacing that explains it.
    let n2: HTMLElement | null = container;
    let depth = 0;
    while (n2 && n2 !== document.documentElement && depth < 11) {
      const s = getComputedStyle(n2);
      const r = n2.getBoundingClientRect();
      const cls = String(n2.className || '').trim().split(/\s+/).slice(0, 2).join('.');
      const short = Math.round(docW - r.right);
      mapRows.push({
        label: `  ${depth}. ${n2.tagName.toLowerCase()}${cls ? '.' + cls : ''}`.slice(0, 26),
        value:
          `r=${Math.round(r.right)} w=${Math.round(r.width)}` +
          ` bar=${n2.offsetWidth - n2.clientWidth} pr=${s.paddingRight} mr=${s.marginRight}` +
          (short > 1 ? `  SHORT ${short}px` : ''),
        bad: short > 1,
      });
      n2 = n2.parentElement;
      depth += 1;
    }
  }

  return [
    ...mapRows,
    { label: 'viewport / doc', value: `${window.innerWidth} / ${docW}  (scrollbar ${window.innerWidth - docW}px)` },
    { label: 'buttons found', value: String(buttons.length), bad: buttons.length < 3 },
    { label: 'switcher right', value: String(Math.round(el.getBoundingClientRect().right)) },
    { label: 'last button right', value: String(lastRight), bad: clipped },
    { label: 'clipped?', value: clipped ? `YES by ${lastRight - docW}px` : 'no', bad: clipped },
    { label: 'first overflow', value: culprit, bad: culprit !== '(none)' },
    { label: 'user agent', value: navigator.userAgent.slice(0, 60) },
  ];
}

function render(): void {
  let panel = document.getElementById(PANEL_ID);
  if (!panel) {
    panel = document.createElement('div');
    panel.id = PANEL_ID;
    panel.style.cssText = [
      'position:fixed', 'right:8px', 'bottom:8px', 'z-index:2147483647',
      'background:rgba(10,12,14,.94)', 'color:#e6edf3', 'font:12px/1.5 Consolas,monospace',
      'padding:10px 12px', 'border:1px solid #30363d', 'border-radius:8px',
      'pointer-events:none', 'max-width:min(560px,92vw)', 'white-space:pre',
    ].join(';');
    document.body.appendChild(panel);
  }
  panel.textContent = measure()
    .map((r) => `${r.bad ? '! ' : '  '}${r.label.padEnd(18)} ${r.value}`)
    .join('\n');
}

export function installLayoutDiag(): void {
  if (process.env.NODE_ENV !== 'development') return;
  if (!/[?&]diag=1\b/.test(window.location.search)) return;

  const schedule = () => requestAnimationFrame(render);
  window.addEventListener('resize', schedule);
  // The toolbar's contents change with the active view, and the fault is view-specific, so
  // re-measure when it mutates rather than only on resize.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();

  // eslint-disable-next-line no-console
  console.info('[dev] layout diagnostics on (?diag=1)');
}
